import React, { useState, useRef, useEffect } from 'react'
import DatePicker, { registerLocale } from 'react-datepicker'
import es from 'date-fns/locale/es'
import CalendarPopup from './CalendarPopup.jsx'
import FullScreenModal from '../../shared_components/FullScreenModal/FullScreenModal'
import 'react-datepicker/dist/react-datepicker.css'

import './SearchDatePicker.scss'

// We can't use a media-query display: none because of some obscure bug
// with having both in the dom, regardless of visibility.
const isMobile = /Mobile/i.test(window.navigator.userAgent)

registerLocale('es', es)

// sometimes the 40 weeks spans 10 months, sometimes it spans 11 months.
const getNumberOfMobileMonthsToShow = (todayDate, minDate) => {
  let months
  months = (todayDate.getFullYear() - minDate.getFullYear()) * 12
  months -= minDate.getMonth()
  months += todayDate.getMonth()
  months += 1
  return months <= 0 ? 0 : months
}

// the library needs this for some reason
// eslint-disable-next-line no-unused-vars -- one off for external lib
const dayName = (date) => 'day'

const SearchDatePicker = ({
  value,
  setValue,
  label,
  placeholder,
  calendarTitle,
  maxDaysInThePast,
  icon = null,
  forceStacked = false,
  disabled = false,
  infoTooltip = null,
  locale,
  lexicon,
}) => {
  const fullScreenModalRef = useRef(null)

  const [isMobileModalOpen, setIsMobileModalOpen] = useState(false)
  const [mobileDateValue, setMobileDateValue] = useState(value || null)

  const getMinDate = () => {
    const date = new Date()
    date.setDate(date.getDate() - maxDaysInThePast)
    return date
  }

  const mobileProps = useRef({
    onClear: () => setMobileDateValue(null),
    selectText: lexicon.get('lmp_placeholder'),
    clearText: lexicon.get('clear'),
  })

  useEffect(() => {
    mobileProps.current = {
      onClear: () => setMobileDateValue(null),
      selectText: lexicon.get('lmp_placeholder'),
      clearText: lexicon.get('clear'),
    }
  }, [lexicon])

  // auto scrolling to either active month or current month
  useEffect(() => {
    if (isMobileModalOpen) {
      if (value) {
        const selectedDayDiv = document.getElementsByClassName(
          'react-datepicker__day--selected'
        )[0]
        fullScreenModalRef.current.scrollTo({
          left: 0,
          top: selectedDayDiv.getBoundingClientRect().top - 200,
          behavior: 'smooth',
        })
      } else {
        fullScreenModalRef.current.scrollTo({
          left: 0,
          top: fullScreenModalRef.current.scrollHeight,
          behavior: 'smooth',
        })
      }
    }
  }, [isMobileModalOpen])

  const mobileDateStr = value ? value.toLocaleDateString() : ''
  const todayDate = new Date()
  const minDate = getMinDate()
  const desktopPreviousMonth = new Date().setDate(0)

  // the popper isn't constraining itself to the screen size, so we have to align it manually
  const desktopDatePickerPlacement = forceStacked
    ? 'bottom-end'
    : 'bottom-center'

  return (
    <div
      className={`SearchDatePicker ${forceStacked ? 'force-stacked' : ''} ${
        disabled ? 'disabled' : ''
      }`}
    >
      {icon && <img src={icon} alt="" />}

      <div className="label-and-input-container">
        <label className="title">{label}</label>

        <div className="mobile-only">
          <textarea
            className={`calendar-input ${mobileDateStr ? '' : 'light-gray'}`}
            value={mobileDateStr || placeholder}
            onClick={() => setIsMobileModalOpen(true)}
            rows="1"
            readOnly
          />

          {isMobileModalOpen && (
            <FullScreenModal
              closeText={lexicon.get('datepicker_submit')}
              setIsModalOpen={setIsMobileModalOpen}
              showX={true}
              additionalCloseHandler={() => {
                setValue(mobileDateValue)
              }}
              scrollingRef={fullScreenModalRef}
            >
              <DatePicker
                selected={mobileDateValue}
                disabledKeyboardNavigation
                onChange={setMobileDateValue}
                monthsShown={getNumberOfMobileMonthsToShow(todayDate, minDate)}
                maxDate={todayDate}
                minDate={minDate}
                dayClassName={dayName}
                openToDate={minDate}
                locale={locale}
                shouldCloseOnSelect={false}
                disabled={disabled}
                inline
                calendarContainer={(props) => (
                  <CalendarPopup
                    calendarTitleText={calendarTitle}
                    additionalClassNames="mobile-layout"
                    mobileLmpDate={mobileDateValue}
                    mobileProps={mobileProps.current}
                    {...props}
                  />
                )}
              />
            </FullScreenModal>
          )}
        </div>

        {!isMobile && (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
          <div
            className={`desktop-only ${locale === 'es' ? 'spanish' : ''}`}
            onClick={(e) => e.preventDefault()}
          >
            <DatePicker
              placeholderText={placeholder}
              selected={value}
              disabledKeyboardNavigation
              onChange={setValue}
              monthsShown={2}
              maxDate={todayDate}
              minDate={minDate}
              disabled={disabled}
              calendarContainer={(props) => (
                <CalendarPopup calendarTitleText={calendarTitle} {...props} />
              )}
              dayClassName={dayName}
              className="calendarInput"
              openToDate={value || desktopPreviousMonth}
              locale={locale}
              shouldCloseOnSelect
              popperPlacement={desktopDatePickerPlacement}
            />
          </div>
        )}

        {!!infoTooltip && (
          <div className="info-tooltip-container">{infoTooltip}</div>
        )}
      </div>
    </div>
  )
}

export default SearchDatePicker
